/**
 * E2EE Module — End-to-End Encryption for CallChat
 * Uses Web Crypto API with RSA-OAEP (key exchange) + AES-GCM (message encryption)
 * Private keys stored in IndexedDB (never leave device)
 */

const DB_NAME = 'callchat_e2ee';
const DB_VERSION = 1;
const KEY_STORE = 'keys';

// ── IndexedDB helpers ──

function openDB() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => { req.result.createObjectStore(KEY_STORE); };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function idbGet(key) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(KEY_STORE, 'readonly');
    const req = tx.objectStore(KEY_STORE).get(key);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function idbPut(key, value) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(KEY_STORE, 'readwrite');
    tx.objectStore(KEY_STORE).put(value, key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

// ── Key Generation ──

const RSA_PARAMS = {
  name: 'RSA-OAEP',
  modulusLength: 2048,
  publicExponent: new Uint8Array([1, 0, 1]),
  hash: 'SHA-256',
};

/**
 * Generate RSA-OAEP key pair. Returns { publicKeyJwk, privateKey (CryptoKey) }
 */
export async function generateKeyPair() {
  const keyPair = await crypto.subtle.generateKey(RSA_PARAMS, true, ['encrypt', 'decrypt']);
  const publicKeyJwk = await crypto.subtle.exportKey('jwk', keyPair.publicKey);
  // Store private key in IndexedDB
  await idbPut('privateKey', keyPair.privateKey);
  await idbPut('publicKeyJwk', publicKeyJwk);
  return publicKeyJwk;
}

/**
 * Get stored private key from IndexedDB
 */
export async function getPrivateKey() {
  return idbGet('privateKey');
}

/**
 * Get stored public key JWK from IndexedDB
 */
export async function getStoredPublicKey() {
  return idbGet('publicKeyJwk');
}

/**
 * Import a JWK public key for encryption
 */
export async function importPublicKey(jwk) {
  return crypto.subtle.importKey('jwk', jwk, RSA_PARAMS, false, ['encrypt']);
}

// ── AES-GCM (symmetric) ──

async function generateAESKey() {
  return crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, true, ['encrypt', 'decrypt']);
}

async function exportAESKey(key) {
  const raw = await crypto.subtle.exportKey('raw', key);
  return new Uint8Array(raw);
}

async function importAESKey(raw) {
  return crypto.subtle.importKey('raw', raw, { name: 'AES-GCM', length: 256 }, false, ['encrypt', 'decrypt']);
}

// ── Encrypt / Decrypt ──

function arrayBufferToBase64(buf) {
  return btoa(String.fromCharCode(...new Uint8Array(buf)));
}

function base64ToArrayBuffer(b64) {
  const binary = atob(b64);
  const buf = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) buf[i] = binary.charCodeAt(i);
  return buf.buffer;
}

/**
 * Encrypt a message for multiple recipients.
 * @param {string} plaintext - The message content
 * @param {Object} recipientPublicKeys - { userId: jwkPublicKey, ... }
 * @returns {{ ciphertext: string, iv: string, encryptedKeys: { [userId]: string } }}
 */
export async function encryptMessage(plaintext, recipientPublicKeys) {
  // 1. Generate random AES key
  const aesKey = await generateAESKey();
  const iv = crypto.getRandomValues(new Uint8Array(12));

  // 2. Encrypt plaintext with AES-GCM
  const encoded = new TextEncoder().encode(plaintext);
  const ciphertext = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, aesKey, encoded);

  // 3. Export AES key and encrypt it with each recipient's RSA public key
  const aesKeyRaw = await exportAESKey(aesKey);
  const encryptedKeys = {};

  for (const [userId, publicKeyJwk] of Object.entries(recipientPublicKeys)) {
    const pubKey = await importPublicKey(publicKeyJwk);
    const encKey = await crypto.subtle.encrypt({ name: 'RSA-OAEP' }, pubKey, aesKeyRaw);
    encryptedKeys[userId] = arrayBufferToBase64(encKey);
  }

  return {
    ciphertext: arrayBufferToBase64(ciphertext),
    iv: arrayBufferToBase64(iv),
    encryptedKeys,
  };
}

/**
 * Decrypt a message using the current user's private key.
 * @param {string} ciphertextB64 - Base64 encoded ciphertext
 * @param {string} ivB64 - Base64 encoded IV
 * @param {string} encryptedKeyB64 - Base64 encoded AES key (encrypted with our public key)
 * @returns {string} - Decrypted plaintext
 */
export async function decryptMessage(ciphertextB64, ivB64, encryptedKeyB64) {
  const privateKey = await getPrivateKey();
  if (!privateKey) throw new Error('No private key found');

  // 1. Decrypt the AES key with our RSA private key
  const encKeyBuf = base64ToArrayBuffer(encryptedKeyB64);
  const aesKeyRaw = await crypto.subtle.decrypt({ name: 'RSA-OAEP' }, privateKey, encKeyBuf);

  // 2. Import the AES key
  const aesKey = await importAESKey(aesKeyRaw);

  // 3. Decrypt the ciphertext
  const iv = base64ToArrayBuffer(ivB64);
  const ciphertext = base64ToArrayBuffer(ciphertextB64);
  const plainBuf = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, aesKey, ciphertext);

  return new TextDecoder().decode(plainBuf);
}

/**
 * Check if E2EE is available (Web Crypto API + IndexedDB)
 */
export function isE2EESupported() {
  return !!(crypto?.subtle && window.indexedDB);
}

/**
 * Check if keys exist in IndexedDB
 */
export async function hasStoredKeys() {
  try {
    const pk = await getPrivateKey();
    return !!pk;
  } catch {
    return false;
  }
}
